/**
 * Compliche analytics — thin wrapper over PostHog.
 *
 * PostHog is initialised in `index.js` ONLY after the user accepts cookie
 * consent. When PostHog isn't loaded every call is a silent no-op so the
 * rest of the codebase can import { track, identify, reset } unconditionally.
 *
 * PII rule: never pass email / name / client_name as properties. We identify
 * users by their server-issued opaque user id only and let PostHog tag the
 * session. Hashing happens server-side; here we just avoid PII fields.
 */

function _ph() {
  if (typeof window === "undefined") return null;
  return window.__compliche_posthog || null;
}

/**
 * Track an event. Property keys should be lowercase_snake_case.
 * Never pass email/name/phone here.
 */
export function track(event, props = {}) {
  const ph = _ph();
  if (!ph) return;
  try { ph.capture(event, props); } catch { /* swallow */ }
}

/** Identify a logged-in user by opaque id. Role is safe to send. */
export function identify(user) {
  const ph = _ph();
  if (!ph || !user || !user.id) return;
  try {
    ph.identify(user.id, {
      role: user.role,
      firm_id: user.firm_id,
      two_factor_enabled: !!user.two_factor_enabled,
    });
    if (user.firm_id) ph.group("firm", user.firm_id);
  } catch { /* swallow */ }
}

/** Reset on logout. */
export function resetAnalytics() {
  const ph = _ph();
  if (!ph) return;
  try { ph.reset(); } catch { /* swallow */ }
}

/** Canonical event names — referenced from across the app. */
export const EVT = {
  // Auth + lifecycle
  user_signed_up: "user_signed_up",
  user_logged_in: "user_logged_in",
  user_logged_out: "user_logged_out",
  firm_onboarding_completed: "firm_onboarding_completed",
  two_factor_enabled: "two_factor_enabled",
  team_member_invited: "team_member_invited",
  settings_updated: "settings_updated",
  // Trial / billing
  trial_started: "trial_started",
  plan_upgrade_started: "plan_upgrade_started",
  plan_upgrade_completed: "plan_upgrade_completed",
  plan_downgrade_started: "plan_downgrade_started",
  trial_cancelled: "trial_cancelled",
  trial_converted: "trial_converted",
  // Flows + clients
  flow_created: "flow_created",
  flow_activated: "flow_activated",
  client_invited: "client_invited",
  client_portal_opened: "client_portal_opened",
  client_portal_completed: "client_portal_completed",
  // Compliance
  compliance_check_run: "compliance_check_run",
  compliance_result_viewed: "compliance_result_viewed",
  document_uploaded: "document_uploaded",
  audit_trail_exported: "audit_trail_exported",
  // API
  api_key_created: "api_key_created",
  api_call_made: "api_call_made",
  // Pro features
  onboarding_pack_downloaded: "onboarding_pack_downloaded",
  aml_flag_raised: "aml_flag_raised",
  aml_flag_reviewed: "aml_flag_reviewed",
  identity_verification_completed: "identity_verification_completed",
  fca_alert_viewed: "fca_alert_viewed",
  quarterly_report_downloaded: "quarterly_report_downloaded",
  // Misc
  page_view: "$pageview",
};
